/**
 * sidebarMutations.ts
 *
 * The ONLY place that writes to both `sidebarOrder` and `syncQueue` for sidebar ops.
 * Every write is atomic — both stores update in one IDB transaction or neither does.
 *
 * IDB RESILIENCE:
 *   Two categories of mutations:
 *
 *   A) Reorder mutations (reorderPrivate, reorderPublic, reorderWorkArea,
 *      reorderShared, reorderTemplates, setSidebarOrder):
 *      These receive the FULL new order array from the component. When IDB is
 *      unavailable, we call the API directly with that array.
 *
 *   B) Add / Remove / Move mutations (addPrivate, removePrivate, etc.):
 *      These read the current order from IDB before modifying it. Without IDB
 *      we cannot construct the full updated array for the API, so we skip
 *      gracefully. Context already shows the optimistic update; the server
 *      order will be corrected on the next full workspace fetch.
 *
 *   All hydration mutations (loadSidebarOrder) skip silently when IDB is
 *   unavailable — data came FROM the API; context already has it.
 *
 * These replace all mutation methods from rootPagesOrderContext:
 *   addPrivatePage      → addPrivatePageMutation()
 *   removePrivatePage   → removePageMutation()
 *   reorderPrivate      → reorderPrivateMutation()
 *   addPublicPage       → addPublicPageMutation()
 *   removePublicPage    → removePageMutation()
 *   reorderPublic       → reorderPublicMutation()
 *   addWorkAreaPage     → addWorkAreaPageMutation()
 *   removeWorkAreaPage  → removePageMutation()
 *   reorderWorkArea     → reorderWorkAreaMutation()
 *   addSharedPage       → addSharedPageMutation()
 *   addTemplatePage     → addTemplatePageMutation()
 *   movePage*           → movePageToMutation()
 *   setSidebarOrder     → setSidebarOrderMutation()
 *   loadSidebarOrder    → loadSidebarOrderMutation() (hydration, no queue entry)
 */

import { appDB, emitIDBChange } from "../db/client";
import { getSidebarOrder } from "../stores/sidebarOrderStore";
import { countPendingOps } from "../stores/syncQueueStore";
import { setPendingCount } from "../stores/syncMetaStore";
import { isIDBAvailable, setIDBStatus, isQuotaError } from "../idbStatus";
import { postWithAuth } from "@/lib/api-helpers";
import type { SidebarReorderPayload } from "../db/schema";

// ─── Internal helpers ─────────────────────────────────────────────

function handleQuotaError(mutationName: string): void {
  setIDBStatus("quota_full");
  console.warn(
    `%c[${mutationName}] IDB quota exceeded mid-session — falling back to direct API`,
    "color: #fb923c; font-weight: bold;"
  );
}

/** Direct API call for reorder mutations where the full new arrays are known. */
async function callSidebarAPI(
  workspaceId: string,
  patch: Partial<Omit<SidebarReorderPayload, "workspaceId">>
): Promise<void> {
  await postWithAuth("/api/workspace/sidebar-order", { workspaceId, ...patch });
}

// ─── Helper: build and enqueue a sidebar reorder op ──────────────

async function enqueueSidebarOp(
  workspaceId: string,
  patch: Partial<Omit<SidebarReorderPayload, "workspaceId">>
): Promise<void> {
  await appDB.syncQueue.add({
    id: crypto.randomUUID(),
    operationType: "SIDEBAR_REORDER",
    payload: { workspaceId, ...patch },
    createdAt: Date.now(),
    isSync: 0 as 0,
    attempts: 0,
    lastAttemptAt: null,
    syncError: null,
    noteId: null,
    workspaceId,
  });
}

// ─────────────────────────────────────────────────────────────────
// PRIVATE PAGES
// ─────────────────────────────────────────────────────────────────

export async function addPrivatePageMutation(
  pageId: string,
  workspaceId: string,
  position?: number,
  skipSync?: boolean
): Promise<void> {
  // Reads current order from IDB before modifying — cannot fallback to API.
  // Skip gracefully; context already shows the optimistic update.
  if (!isIDBAvailable()) return;

  const tables = skipSync ? [appDB.sidebarOrder] : [appDB.sidebarOrder, appDB.syncQueue];

  try {
    await appDB.transaction("rw", tables, async () => {
      const current = await getSidebarOrder();
      if (current.private.includes(pageId)) return;

      const newPrivate = [...current.private];
      if (position !== undefined && position >= 0 && position <= newPrivate.length) {
        newPrivate.splice(position, 0, pageId);
      } else {
        newPrivate.push(pageId);
      }

      await appDB.sidebarOrder.put({
        ...current,
        private: newPrivate,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });

      if (!skipSync) {
        await enqueueSidebarOp(workspaceId, { private: newPrivate });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addPrivatePageMutation");
      return; // Cannot reconstruct full order array — skip
    }
    throw err;
  }
}

export async function removePrivatePageMutation(
  pageId: string,
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    // Read INSIDE the transaction to avoid the stale-spread race documented on
    // addTemplatePageMutation. Matches addPrivatePageMutation's pattern.
    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      const current = await getSidebarOrder();
      const newPrivate = current.private.filter((id) => id !== pageId);
      if (newPrivate.length === current.private.length) return;

      await appDB.sidebarOrder.put({
        ...current,
        private: newPrivate,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { private: newPrivate });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("removePrivatePageMutation");
      return;
    }
    throw err;
  }
}

export async function reorderPrivateMutation(
  newOrder: string[],
  workspaceId: string,
  skipSync = false,
): Promise<void> {
  // Full new order received from component — can fallback to API directly.
  if (!isIDBAvailable()) {
    if (!skipSync) await callSidebarAPI(workspaceId, { private: newOrder });
    return;
  }

  const tables = skipSync ? [appDB.sidebarOrder] : [appDB.sidebarOrder, appDB.syncQueue];

  try {
    await appDB.transaction("rw", tables, async () => {
      const current = await getSidebarOrder();
      await appDB.sidebarOrder.put({
        ...current,
        private: newOrder,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      if (!skipSync) {
        await enqueueSidebarOp(workspaceId, { private: newOrder });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("reorderPrivateMutation");
      if (!skipSync) await callSidebarAPI(workspaceId, { private: newOrder });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// PUBLIC PAGES
// ─────────────────────────────────────────────────────────────────

export async function addPublicPageMutation(
  pageId: string,
  workspaceId: string,
  position?: number,
  skipSync?: boolean
): Promise<void> {
  if (!isIDBAvailable()) return;

  const tables = skipSync ? [appDB.sidebarOrder] : [appDB.sidebarOrder, appDB.syncQueue];

  try {
    await appDB.transaction("rw", tables, async () => {
      const current = await getSidebarOrder();
      if (current.public.includes(pageId)) return;

      const newPublic = [...current.public];
      if (position !== undefined && position >= 0 && position <= newPublic.length) {
        newPublic.splice(position, 0, pageId);
      } else {
        newPublic.push(pageId);
      }

      await appDB.sidebarOrder.put({
        ...current,
        public: newPublic,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });

      if (!skipSync) {
        await enqueueSidebarOp(workspaceId, { public: newPublic });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addPublicPageMutation");
      return;
    }
    throw err;
  }
}

export async function removePublicPageMutation(
  pageId: string,
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    // Read INSIDE the transaction to avoid the stale-spread race documented on
    // addTemplatePageMutation. Matches addPublicPageMutation's pattern.
    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      const current = await getSidebarOrder();
      const newPublic = current.public.filter((id) => id !== pageId);
      if (newPublic.length === current.public.length) return;

      await appDB.sidebarOrder.put({
        ...current,
        public: newPublic,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { public: newPublic });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("removePublicPageMutation");
      return;
    }
    throw err;
  }
}

export async function reorderPublicMutation(
  newOrder: string[],
  workspaceId: string,
  skipSync = false,
): Promise<void> {
  if (!isIDBAvailable()) {
    if (!skipSync) await callSidebarAPI(workspaceId, { public: newOrder });
    return;
  }

  const tables = skipSync ? [appDB.sidebarOrder] : [appDB.sidebarOrder, appDB.syncQueue];

  try {
    await appDB.transaction("rw", tables, async () => {
      const current = await getSidebarOrder();
      await appDB.sidebarOrder.put({
        ...current,
        public: newOrder,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      if (!skipSync) {
        await enqueueSidebarOp(workspaceId, { public: newOrder });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("reorderPublicMutation");
      if (!skipSync) await callSidebarAPI(workspaceId, { public: newOrder });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// WORK AREA PAGES
// ─────────────────────────────────────────────────────────────────

export async function addWorkAreaPageMutation(
  workAreaId: string,
  pageId: string,
  workspaceId: string,
  position?: number,
  skipSync?: boolean
): Promise<void> {
  if (!isIDBAvailable()) return;

  const tables = skipSync ? [appDB.sidebarOrder] : [appDB.sidebarOrder, appDB.syncQueue];

  try {
    await appDB.transaction("rw", tables, async () => {
      const current = await getSidebarOrder();
      const workAreaPages = current.workArea[workAreaId] ?? [];
      if (workAreaPages.includes(pageId)) return;

      const updated = [...workAreaPages];
      if (position !== undefined && position >= 0 && position <= updated.length) {
        updated.splice(position, 0, pageId);
      } else {
        updated.push(pageId);
      }

      const newWorkArea = { ...current.workArea, [workAreaId]: updated };

      await appDB.sidebarOrder.put({
        ...current,
        workArea: newWorkArea,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });

      if (!skipSync) {
        await enqueueSidebarOp(workspaceId, { workArea: newWorkArea });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addWorkAreaPageMutation");
      return;
    }
    throw err;
  }
}

export async function removeWorkAreaPageMutation(
  pageId: string,
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    // Read INSIDE the transaction to avoid the stale-spread race documented on
    // addTemplatePageMutation. Matches addWorkAreaPageMutation's pattern.
    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      const current = await getSidebarOrder();
      const newWorkArea = { ...current.workArea };
      let changed = false;

      for (const wId of Object.keys(newWorkArea)) {
        const filtered = newWorkArea[wId]?.filter((id) => id !== pageId);
        if (filtered?.length !== newWorkArea[wId]?.length) {
          newWorkArea[wId] = filtered || [];
          changed = true;
        }
      }

      if (!changed) return;

      await appDB.sidebarOrder.put({
        ...current,
        workArea: newWorkArea,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { workArea: newWorkArea });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("removeWorkAreaPageMutation");
      return;
    }
    throw err;
  }
}

export async function reorderWorkAreaMutation(
  newOrder: Record<string, string[]>,
  workspaceId: string,
  skipSync = false,
): Promise<void> {
  if (!isIDBAvailable()) {
    if (!skipSync) await callSidebarAPI(workspaceId, { workArea: newOrder });
    return;
  }

  const tables = skipSync ? [appDB.sidebarOrder] : [appDB.sidebarOrder, appDB.syncQueue];

  try {
    await appDB.transaction("rw", tables, async () => {
      const current = await getSidebarOrder();
      await appDB.sidebarOrder.put({
        ...current,
        workArea: newOrder,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      if (!skipSync) {
        await enqueueSidebarOp(workspaceId, { workArea: newOrder });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("reorderWorkAreaMutation");
      if (!skipSync) await callSidebarAPI(workspaceId, { workArea: newOrder });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// SHARED PAGES
// ─────────────────────────────────────────────────────────────────

export async function addSharedPageMutation(
  pageId: string,
  workspaceId: string,
  position?: number
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    // Read INSIDE the transaction to avoid the stale-spread race documented on
    // addTemplatePageMutation. Matches addPrivatePageMutation's pattern.
    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      const current = await getSidebarOrder();
      if (current.shared.includes(pageId)) return;

      const newShared = [...current.shared];
      if (position !== undefined && position >= 0 && position <= newShared.length) {
        newShared.splice(position, 0, pageId);
      } else {
        newShared.push(pageId);
      }

      await appDB.sidebarOrder.put({
        ...current,
        shared: newShared,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { shared: newShared });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addSharedPageMutation");
      return;
    }
    throw err;
  }
}

export async function removeSharedPageMutation(
  pageId: string,
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    // Read INSIDE the transaction to avoid the stale-spread race documented on
    // addTemplatePageMutation. Matches addPrivatePageMutation's pattern.
    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      const current = await getSidebarOrder();
      const newShared = current.shared.filter((id) => id !== pageId);
      if (newShared.length === current.shared.length) return;

      await appDB.sidebarOrder.put({
        ...current,
        shared: newShared,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { shared: newShared });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("removeSharedPageMutation");
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// TEMPLATE PAGES
// ─────────────────────────────────────────────────────────────────

export async function addTemplatePageMutation(
  pageId: string,
  workspaceId: string,
  position?: number,
  skipSync = false,
): Promise<void> {
  if (!isIDBAvailable()) return;

  // skipSync: local-only IDB write, for callers whose server-side templatePageIds
  // is already handled elsewhere (batch-create or the drag-and-drop move).
  const tables = skipSync ? [appDB.sidebarOrder] : [appDB.sidebarOrder, appDB.syncQueue];

  try {
    await appDB.transaction("rw", tables, async () => {
      const current = await getSidebarOrder();
      if (current.template.includes(pageId)) return;

      const newTemplate = [...current.template];
      if (position !== undefined && position >= 0 && position <= newTemplate.length) {
        newTemplate.splice(position, 0, pageId);
      } else {
        newTemplate.push(pageId);
      }

      await appDB.sidebarOrder.put({
        ...current,
        template: newTemplate,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      if (!skipSync) {
        await enqueueSidebarOp(workspaceId, { template: newTemplate });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addTemplatePageMutation");
      return;
    }
    throw err;
  }
}

export async function removeTemplatePageMutation(
  pageId: string,
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    // Read INSIDE the transaction to avoid the same race documented on
    // addTemplatePageMutation — the spread of `current` would otherwise
    // carry stale arrays forward when called concurrently with another
    // sidebarOrder writer.
    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      const current = await getSidebarOrder();
      const newTemplate = current.template.filter((id) => id !== pageId);
      if (newTemplate.length === current.template.length) return;

      await appDB.sidebarOrder.put({
        ...current,
        template: newTemplate,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { template: newTemplate });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("removeTemplatePageMutation");
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// REMOVE FROM ALL LISTS (replaces rootPagesOrderContext.removePage)
// ─────────────────────────────────────────────────────────────────

/**
 * Removes a page from every list it appears in.
 * Replaces: rootPagesOrderContext.removePage()
 */
export async function removePageFromAllMutation(
  pageId: string,
  workspaceId: string,
  skipSync = false,
): Promise<void> {
  if (!isIDBAvailable()) return;

  const tables = skipSync ? [appDB.sidebarOrder] : [appDB.sidebarOrder, appDB.syncQueue];

  try {
    await appDB.transaction("rw", tables, async () => {
      const current = await getSidebarOrder();

      const newPrivate = current.private.filter((id) => id !== pageId);
      const newPublic = current.public.filter((id) => id !== pageId);
      const newShared = current.shared.filter((id) => id !== pageId);
      const newTemplate = current.template.filter((id) => id !== pageId);
      const newFavourites = (current.favourites ?? []).filter((id) => id !== pageId);
      const newWorkArea = Object.fromEntries(
        Object.entries(current.workArea).map(([wId, ids]) => [
          wId,
          ids.filter((id) => id !== pageId),
        ])
      );

      await appDB.sidebarOrder.put({
        ...current,
        private: newPrivate,
        public: newPublic,
        shared: newShared,
        template: newTemplate,
        favourites: newFavourites,
        workArea: newWorkArea,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });

      if (!skipSync) {
        await enqueueSidebarOp(workspaceId, {
          private: newPrivate,
          public: newPublic,
          shared: newShared,
          template: newTemplate,
          favourites: newFavourites,
          workArea: newWorkArea,
        });
      }
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("removePageFromAllMutation");
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// REORDER SHARED PAGES
// ─────────────────────────────────────────────────────────────────

export async function reorderSharedMutation(
  newOrder: string[],
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) {
    await callSidebarAPI(workspaceId, { shared: newOrder });
    return;
  }

  try {
    const current = await getSidebarOrder();

    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      await appDB.sidebarOrder.put({
        ...current,
        shared: newOrder,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { shared: newOrder });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("reorderSharedMutation");
      await callSidebarAPI(workspaceId, { shared: newOrder });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// REORDER TEMPLATE PAGES
// ─────────────────────────────────────────────────────────────────

export async function reorderTemplatesMutation(
  newOrder: string[],
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) {
    await callSidebarAPI(workspaceId, { template: newOrder });
    return;
  }

  try {
    const current = await getSidebarOrder();

    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      await appDB.sidebarOrder.put({
        ...current,
        template: newOrder,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { template: newOrder });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("reorderTemplatesMutation");
      await callSidebarAPI(workspaceId, { template: newOrder });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// MOVE PAGE (remove from all lists + add to target in one op)
// ─────────────────────────────────────────────────────────────────

/**
 * Atomically removes pageId from every list and inserts it into private.
 * Replaces: rootPagesOrderContext.movePageToPrivate()
 *
 * @param skipSync  When true, writes IDB only — no SIDEBAR_REORDER op enqueued.
 *                  Use this when the backend already handles sidebar cleanup
 *                  (e.g. drag-and-drop API updates blockIdArrays server-side).
 */
export async function movePageToPrivateMutation(
  pageId: string,
  workspaceId: string,
  position?: number,
  skipSync = false,
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    const current = await getSidebarOrder();

    const newPrivate = current.private.filter((id) => id !== pageId);
    if (position !== undefined && position >= 0 && position <= newPrivate.length) {
      newPrivate.splice(position, 0, pageId);
    } else {
      newPrivate.push(pageId);
    }

    const newPublic = current.public.filter((id) => id !== pageId);
    const newShared = current.shared.filter((id) => id !== pageId);
    const newTemplate = current.template.filter((id) => id !== pageId);
    const newWorkArea = Object.fromEntries(
      Object.entries(current.workArea).map(([wId, ids]) => [wId, ids.filter((id) => id !== pageId)])
    );

    const updatedRecord = {
      ...current,
      private: newPrivate,
      public: newPublic,
      shared: newShared,
      template: newTemplate,
      workArea: newWorkArea,
      isSync: 0 as 0,
      localUpdatedAt: Date.now(),
    };

    if (skipSync) {
      await appDB.sidebarOrder.put(updatedRecord);
    } else {
      await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
        await appDB.sidebarOrder.put(updatedRecord);
        await enqueueSidebarOp(workspaceId, {
          private: newPrivate,
          public: newPublic,
          shared: newShared,
          template: newTemplate,
          workArea: newWorkArea,
        });
      });
    }

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("movePageToPrivateMutation");
      return;
    }
    throw err;
  }
}

/**
 * Atomically removes pageId from every section list and inserts it into the
 * given work area. The work-area counterpart of movePageToPrivate/PublicMutation
 * — and, like those, it leaves `favourites` alone, since being in a work area
 * and being favourited are independent. (removePageFromAllMutation would strip
 * the favourite, which is why this is not built on top of it.)
 *
 * @param skipSync  When true, writes IDB only — no SIDEBAR_REORDER op enqueued.
 */
export async function movePageToWorkAreaMutation(
  workAreaId: string,
  pageId: string,
  workspaceId: string,
  position?: number,
  skipSync = false,
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    const current = await getSidebarOrder();

    const newPrivate = current.private.filter((id) => id !== pageId);
    const newPublic = current.public.filter((id) => id !== pageId);
    const newShared = current.shared.filter((id) => id !== pageId);
    const newTemplate = current.template.filter((id) => id !== pageId);

    const newWorkArea = Object.fromEntries(
      Object.entries(current.workArea).map(([wId, ids]) => [wId, ids.filter((id) => id !== pageId)])
    );
    const destination = [...(newWorkArea[workAreaId] ?? [])];
    if (position !== undefined && position >= 0 && position <= destination.length) {
      destination.splice(position, 0, pageId);
    } else {
      destination.push(pageId);
    }
    newWorkArea[workAreaId] = destination;

    const updatedRecord = {
      ...current,
      private: newPrivate,
      public: newPublic,
      shared: newShared,
      template: newTemplate,
      workArea: newWorkArea,
      isSync: 0 as 0,
      localUpdatedAt: Date.now(),
    };

    if (skipSync) {
      await appDB.sidebarOrder.put(updatedRecord);
    } else {
      await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
        await appDB.sidebarOrder.put(updatedRecord);
        await enqueueSidebarOp(workspaceId, {
          private: newPrivate,
          public: newPublic,
          shared: newShared,
          template: newTemplate,
          workArea: newWorkArea,
        });
      });
    }

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("movePageToWorkAreaMutation");
      return;
    }
    throw err;
  }
}

/**
 * Atomically removes pageId from every list and inserts it into public.
 * Replaces: rootPagesOrderContext.movePageToPublic()
 *
 * @param skipSync  When true, writes IDB only — no SIDEBAR_REORDER op enqueued.
 *                  Use this when the backend already handles sidebar cleanup
 *                  (e.g. drag-and-drop API updates blockIdArrays server-side).
 */
export async function movePageToPublicMutation(
  pageId: string,
  workspaceId: string,
  position?: number,
  skipSync = false,
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    const current = await getSidebarOrder();

    const newPublic = current.public.filter((id) => id !== pageId);
    if (position !== undefined && position >= 0 && position <= newPublic.length) {
      newPublic.splice(position, 0, pageId);
    } else {
      newPublic.push(pageId);
    }

    const newPrivate = current.private.filter((id) => id !== pageId);
    const newShared = current.shared.filter((id) => id !== pageId);
    const newTemplate = current.template.filter((id) => id !== pageId);
    const newWorkArea = Object.fromEntries(
      Object.entries(current.workArea).map(([wId, ids]) => [wId, ids.filter((id) => id !== pageId)])
    );

    const updatedRecord = {
      ...current,
      private: newPrivate,
      public: newPublic,
      shared: newShared,
      template: newTemplate,
      workArea: newWorkArea,
      isSync: 0 as 0,
      localUpdatedAt: Date.now(),
    };

    if (skipSync) {
      await appDB.sidebarOrder.put(updatedRecord);
    } else {
      await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
        await appDB.sidebarOrder.put(updatedRecord);
        await enqueueSidebarOp(workspaceId, {
          private: newPrivate,
          public: newPublic,
          shared: newShared,
          template: newTemplate,
          workArea: newWorkArea,
        });
      });
    }

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("movePageToPublicMutation");
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// SIDEBAR ORDER (collapsed/expanded list)
// ─────────────────────────────────────────────────────────────────

export async function setSidebarOrderMutation(
  order: string[],
  workspaceId: string
): Promise<void> {
  if (!isIDBAvailable()) {
    await callSidebarAPI(workspaceId, { sidebar: order });
    return;
  }

  try {
    const current = await getSidebarOrder();

    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      await appDB.sidebarOrder.put({
        ...current,
        sidebar: order,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { sidebar: order });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("setSidebarOrderMutation");
      await callSidebarAPI(workspaceId, { sidebar: order });
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// INITIAL HYDRATION (from WorkspaceResponse — no queue entry)
// ─────────────────────────────────────────────────────────────────

/**
 * Load sidebar order from the API WorkspaceResponse.
 * Marks as isSync: 1 — already in MongoDB.
 * Does NOT add to syncQueue.
 *
 * If IDB is unavailable, skip silently — context already holds the data.
 */
export async function loadSidebarOrderMutation(data: {
  workspaceId: string;
  private_pages: string[];
  public_pages: string[];
  workArea: Record<string, string[]>;
  shared_pages: string[];
  template_pages?: string[];
  favourite_pages?: string[];
  sidebar_order: string[];
}): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    await appDB.sidebarOrder.put({
      id: "global",
      workspaceId: data.workspaceId,
      private: data.private_pages,
      public: data.public_pages,
      workArea: data.workArea,
      shared: data.shared_pages,
      template: data.template_pages ?? [],
      favourites: data.favourite_pages ?? [],
      sidebar: data.sidebar_order,
      isSync: 1 as 1,
      localUpdatedAt: Date.now(),
    });
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("loadSidebarOrderMutation");
      return; // No API fallback — data came FROM the API; context already has it
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// FAVOURITE PAGES
// ─────────────────────────────────────────────────────────────────

export async function addFavouritePageMutation(
  pageId: string,
  workspaceId: string,
): Promise<void> {
  if (!isIDBAvailable()) {
    await callSidebarAPI(workspaceId, { favourites: [pageId] });
    return;
  }

  try {
    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      const current = await getSidebarOrder();
      const existingFavourites = current.favourites ?? [];
      if (existingFavourites.includes(pageId)) return;

      const newFavourites = [...existingFavourites, pageId];

      await appDB.sidebarOrder.put({
        ...current,
        favourites: newFavourites,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { favourites: newFavourites });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addFavouritePageMutation");
      return;
    }
    throw err;
  }
}

export async function removeFavouritePageMutation(
  pageId: string,
  workspaceId: string,
): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    await appDB.transaction("rw", [appDB.sidebarOrder, appDB.syncQueue], async () => {
      const current = await getSidebarOrder();
      const newFavourites = (current.favourites ?? []).filter((id) => id !== pageId);
      if (newFavourites.length === (current.favourites ?? []).length) return;

      await appDB.sidebarOrder.put({
        ...current,
        favourites: newFavourites,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await enqueueSidebarOp(workspaceId, { favourites: newFavourites });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("removeFavouritePageMutation");
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// ROOT BLOCKS NEXT TO A SIBLING
// ─────────────────────────────────────────────────────────────────

/** Local order only (no SIDEBAR_REORDER op): batch-create already filed these blocks. */
export async function addRootBlocksAfterMutation(neighbourId: string, newIds: string[]): Promise<void> {
  if (!isIDBAvailable()) return;

  const insertAfter = (list: string[]) => {
    const rest = list.filter((id) => !newIds.includes(id));
    const at = rest.indexOf(neighbourId);
    return [...rest.slice(0, at + 1), ...newIds, ...rest.slice(at + 1)];
  };

  try {
    await appDB.transaction("rw", [appDB.sidebarOrder], async () => {
      const current = await getSidebarOrder();
      const workAreaId = Object.keys(current.workArea).find((id) => current.workArea[id]?.includes(neighbourId));
      let next: Partial<typeof current> | null = null;
      if (current.public.includes(neighbourId)) next = { public: insertAfter(current.public) };
      else if (current.private.includes(neighbourId)) next = { private: insertAfter(current.private) };
      else if (workAreaId) {
        next = { workArea: { ...current.workArea, [workAreaId]: insertAfter(current.workArea[workAreaId] ?? []) } };
      }
      if (!next) return;
      await appDB.sidebarOrder.put({
        ...current,
        ...next,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
    });
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("addRootBlocksAfterMutation");
      return;
    }
    throw err;
  }
}
